import Link from 'next/link';
import React from 'react';
import { ArrowRight, ArrowLeft, Loader2 } from '@/lib/icons';

interface ButtonCtaProps {
  href?: string;
  variant?: 'primary' | 'secondary' | 'standard' | 'utility';
  showArrow?: boolean;
  arrowPosition?: 'left' | 'right';
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  external?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  target?: string;
  rel?: string;
  'aria-label'?: string;
  loading?: boolean;
}

export const ButtonCta: React.FC<ButtonCtaProps> = ({
  href,
  variant = 'primary',
  showArrow = false,
  arrowPosition = 'right',
  children,
  onClick,
  className = '',
  external = false,
  disabled = false,
  type = 'button',
  target,
  rel,
  'aria-label': ariaLabel,
  loading = false,
}) => {
  // Determine if link is external
  const isExternal = external || href?.startsWith('http');
  const needsNewTab = isExternal || href?.includes('.pdf') || href?.includes('.zip');
  const needsDownload = href?.includes('.pdf') || href?.includes('.zip');
  
  // Arrow size based on variant
  const arrowSize = (variant === 'primary' || variant === 'secondary') 
    ? 'h-5 w-5' 
    : 'h-4 w-4';
  
  // Build className
  const baseClass = `btn-${variant} group`;
  const paddingClass = showArrow ? 
    (arrowPosition === 'left' ? 'btn-arrow-left' : 'btn-arrow-right') : '';
  const finalClassName = `${baseClass} ${paddingClass} ${className}`.trim();
  
  // Arrow component
  const ArrowIcon = arrowPosition === 'left' ? ArrowLeft : ArrowRight;
  const arrowClass = arrowPosition === 'left' ? 'arrow-icon arrow-icon-left' : 'arrow-icon';
  
  // Content with loading state
  const content = (
    <>
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <>
          {showArrow && arrowPosition === 'left' && (
            <ArrowIcon className={`${arrowClass} ${arrowSize} mr-2`} />
          )}
          {children}
          {showArrow && arrowPosition === 'right' && (
            <ArrowIcon className={`${arrowClass} ${arrowSize} ml-2`} />
          )}
        </>
      )}
    </>
  );
  
  // Common props
  const commonProps = {
    className: finalClassName,
    'aria-label': ariaLabel,
    'aria-disabled': disabled || loading,
  };
  
  // Handle external or file links
  if (needsNewTab && href) {
    return (
      <a
        href={href}
        target={target || '_blank'}
        rel={rel || 'noopener noreferrer'}
        download={needsDownload || undefined}
        {...commonProps}
      >
        {content}
      </a>
    );
  }
  
  // Handle internal links
  if (href) {
    return (
      <Link 
        href={href}
        prefetch={true}
        {...commonProps}
      >
        {content}
      </Link>
    );
  }
  
  // Handle button
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      {...commonProps}
    >
      {content}
    </button>
  );
};